import type { IconType } from 'react-icons';
import {
    FcBarChart,
    FcCalculator,
    FcCalendar,
    FcClock,
    FcComboChart,
    FcCurrencyExchange,
    FcDoughnutChart,
    FcFlowChart,
    FcIdea,
    FcInspection,
    FcLandscape,
    FcList,
    FcOrganization,
    FcSettings,
} from 'react-icons/fc';
import {
    LuFolderKanban,
    LuBookOpen,
    LuHeartHandshake,
    LuHouse,
    LuLandmark,
    LuSettings,
} from 'react-icons/lu';
import { RootInhabitant } from 'src/api/Root';
import { MemberInhabitant } from 'src/api/Member';
import type { Role } from 'src/api/Role';
import { OrgUtil } from 'src/data/OrgUtil';
import { PathUtil } from 'src/data/PathUtil';

export interface HeaderMenuNode {
    nameKey: string;
    link?: string;
    nodes?: HeaderMenuNode[];
    icon?: IconType;
    expectedRole?: Role;
}

export interface HeaderLinkNode extends HeaderMenuNode {
    link: string;
}

export type HeaderBadgeCounts = Record<string, number | undefined>;

export const PUBLIC_MENU_NODES: HeaderMenuNode[] = [
    { nameKey: 'header.public.product', link: '/#product' },
    { nameKey: 'header.public.howItWorks', link: '/#how-it-works' },
    { nameKey: 'header.public.forNgos', link: '/#for-ngos' },
    {
        nameKey: 'header.public.resources',
        nodes: [
            { nameKey: 'header.nav.documentation', link: '/documents/explanation.pdf', icon: LuBookOpen },
            { nameKey: 'header.open.holidays', link: '/timesheet/holidays', icon: FcLandscape },
            { nameKey: 'header.open.worktime', link: '/timesheet/worktime', icon: FcCalculator },
            { nameKey: 'header.open.rates', link: '/rates', icon: FcCurrencyExchange },
        ],
    },
];

export const AUTHENTICATED_MENU_NODES: HeaderMenuNode[] = [
    { nameKey: 'header.nav.home', link: '/', icon: LuHouse },
    {
        nameKey: 'header.nav.fundraising',
        expectedRole: MemberInhabitant,
        icon: LuHeartHandshake,
        nodes: [
            { nameKey: 'header.nav.fundraisingOverview', link: '/fundraising/', icon: FcComboChart },
            { nameKey: 'header.nav.fundraisingPerProject', link: '/fundraising/projects/', icon: FcBarChart },
            { nameKey: 'header.nav.commitments', link: '/fundraising/commitments/' },
            { nameKey: 'header.nav.partnerCarryover', link: '/transfers/moves/' },
            { nameKey: 'header.nav.donors', link: '/fundraising/donors' },
        ],
    },
    {
        nameKey: 'header.nav.projectManagement',
        expectedRole: MemberInhabitant,
        icon: LuFolderKanban,
        nodes: [
            { nameKey: 'header.nav.projectsOverview', link: '/projects/overview/', icon: FcDoughnutChart },
            { nameKey: 'header.nav.perProjectView', link: '/projects/overview/projects/', icon: FcBarChart },
            { nameKey: 'header.nav.projects', link: '/projects/' },
            { nameKey: 'header.nav.partners', link: '/partners/' },
            { nameKey: 'header.nav.consultants', link: '/partners/consultants/' },
            { nameKey: 'header.nav.expenses', link: '/partners/expenses/' },
        ],
    },
    {
        nameKey: 'header.nav.finance',
        expectedRole: MemberInhabitant,
        icon: LuLandmark,
        nodes: [
            //{ nameKey: 'header.nav.projectFundsView', link: '/funds/provisions/', icon: FcPieChart },
            { nameKey: 'header.nav.fundAllocationView', link: '/funds/moves/overview/', icon: FcList },
            { nameKey: 'header.nav.costCenters', link: '/donations/codes' },
            { nameKey: 'header.nav.funds', link: '/funds/' },
            { nameKey: 'header.nav.fundAllocations', link: '/funds/moves/' },
            { nameKey: 'header.nav.transfers', link: '/transfers/' },
            { nameKey: 'header.nav.invoiceExpenses', link: '/expenses' },
        ],
    },
    {
        nameKey: 'header.nav.administration',
        icon: LuSettings,
        expectedRole: MemberInhabitant,
        nodes: [
            { nameKey: 'header.nav.timesheet', link: '/timesheet/', icon: FcClock },
            { nameKey: 'header.nav.absences', link: '/timesheet/absences', icon: FcCalendar },
            { nameKey: '' },
            { nameKey: 'header.nav.approvals', link: '/approvals', icon: FcInspection },
            { nameKey: 'header.nav.organization', link: '/organization', icon: FcOrganization },
            { nameKey: 'header.nav.directReports', link: '/admin/users/direct-reports/', icon: FcFlowChart },
            { nameKey: '' },
            { nameKey: 'header.open.holidays', link: '/timesheet/holidays', icon: FcLandscape },
            { nameKey: 'header.open.worktime', link: '/timesheet/worktime', icon: FcCalculator },
            { nameKey: 'header.open.rates', link: '/rates', icon: FcCurrencyExchange },
            { nameKey: '' },
            { nameKey: 'header.nav.allOrganizations', link: '/admin/organizations', icon: FcSettings, expectedRole: RootInhabitant },
            { nameKey: 'header.nav.documentation', link: '/documents/explanation.pdf', icon: LuBookOpen },
            { nameKey: 'header.nav.development', link: '/dev', icon: FcIdea },
        ],
    },
];

export const EXTERN_MENU_NODES: HeaderMenuNode[] = [
    { nameKey: 'header.nav.home', link: '/', icon: LuHouse },
    { nameKey: 'header.open.holidays', link: '/timesheet/holidays', icon: FcLandscape },
];

export const getMenuNodesForRole = (role?: Role | null): HeaderMenuNode[] => {
    if (!role) return PUBLIC_MENU_NODES;
    if (OrgUtil.isRoleExtern(role)) return EXTERN_MENU_NODES;
    return AUTHENTICATED_MENU_NODES;
};

export const getAllowedMenuNodes = (
    nodes: HeaderMenuNode[],
    role?: Role,
): HeaderMenuNode[] => nodes.filter((node) => OrgUtil.isAllowed(role, node.expectedRole));

export const isMenuNodeActive = (node: HeaderMenuNode, path: string): boolean => {
    const linkIsActive = PathUtil.dropSlashesAndId(node.link ?? 'x') === path;
    const childIsActive = node.nodes?.some((child) =>
        PathUtil.dropSlashesAndId(child.link ?? 'x') === path
    ) ?? false;

    return linkIsActive || childIsActive;
};

export const isMenuLinkActive = (link: string | undefined, path: string): boolean =>
    PathUtil.dropSlashesAndId(link ?? '') === path;

export const isHeaderLinkNode = (node: HeaderMenuNode): node is HeaderLinkNode =>
    Boolean(node.link);

export const getMenuNodeBadgeCount = (node: HeaderMenuNode, badgeCounts: HeaderBadgeCounts): number => {
    const directCount = badgeCounts[node.nameKey] ?? 0;
    const childCount = node.nodes?.reduce((sum, child) => sum + getMenuNodeBadgeCount(child, badgeCounts), 0) ?? 0;
    return directCount + childCount;
};
